import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { PHASES } from "../../data/curriculum";

export const Curriculum = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="program" data-testid="curriculum-section" className="py-24 sm:py-28 bg-background bg-grid-light">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          overline="Course Curriculum"
          title="What You'll Learn During the 45-Day Program"
          sub="Eleven structured phases — from business foundations to a complete analytics dashboard. Every topic, in the open."
        />
        <div className="mt-14 space-y-4">
          {PHASES.map((p, i) => {
            const open = openIdx === i;
            return (
              <Reveal key={p.n} delay={Math.min(i * 0.04, 0.2)}>
                <div
                  data-testid={`curriculum-phase-${p.n}`}
                  className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                    open ? "border-electric/50 shadow-xl shadow-electric/5" : "border-slate-200/80 hover:border-electric/30"
                  } ${p.featured ? "ring-1 ring-cyanx/40" : ""}`}
                >
                  <button
                    data-testid={`curriculum-accordion-phase-${p.n}`}
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full flex items-center gap-4 sm:gap-6 px-5 sm:px-8 py-6 text-left"
                  >
                    <span className={`font-display font-extrabold text-3xl sm:text-4xl shrink-0 transition-colors ${open ? "text-electric" : "text-slate-200"}`}>
                      {p.n}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-display font-bold text-navy text-base sm:text-lg leading-snug">{p.title}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-bold uppercase tracking-widest text-electric bg-electric/5 rounded-full px-2.5 py-0.5">
                          {p.items.length} Topics
                        </span>
                        {p.featured && (
                          <span className="text-[11px] font-bold uppercase tracking-widest text-navy bg-cyanx/25 rounded-full px-2.5 py-0.5">Core Module</span>
                        )}
                      </span>
                    </span>
                    <ChevronDown size={20} className={`shrink-0 text-slate-400 transition-transform duration-300 ${open ? "rotate-180 text-electric" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-slate-100"
                      >
                        <div className="px-5 sm:px-8 py-7">
                          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-2.5">
                            {p.items.map((item) => (
                              <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
